/**
 * Drag-to-compare before/after slider for the "LP: Proof Before/After" section.
 *
 * Drives the drag with pointer math against the wrapper's own bounding rect instead of
 * relying on the native <input type="range"> click-to-position behavior: in this section's
 * layout (portrait media, absolutely-positioned full-cover input inside a CSS grid), that
 * native behavior miscalculates and snaps the value to 0 on any click, regardless of position.
 * The range input is kept, but only for keyboard access (arrow keys); pointer-events are
 * disabled on it so it never runs its own (buggy) click/drag handling.
 */
class LpProofSlider extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('[data-el="wrapper"]');
    this.range = this.querySelector('[data-el="range"]');
    if (!this.wrapper || !this.range) return;

    this.range.addEventListener('input', this.#onRangeInput);
    this.wrapper.addEventListener('pointerdown', this.#onPointerDown);
  }

  disconnectedCallback() {
    this.range?.removeEventListener('input', this.#onRangeInput);
    this.wrapper?.removeEventListener('pointerdown', this.#onPointerDown);
  }

  #onRangeInput = () => {
    this.#setCompare(Number(this.range.value));
  };

  #onPointerDown = (event) => {
    event.preventDefault();
    this.wrapper.setPointerCapture(event.pointerId);
    this.#updateFromPointer(event);
    this.wrapper.addEventListener('pointermove', this.#onPointerMove);
    this.wrapper.addEventListener('pointerup', this.#onPointerUp);
    this.wrapper.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    this.#updateFromPointer(event);
  };

  #onPointerUp = (event) => {
    this.wrapper.removeEventListener('pointermove', this.#onPointerMove);
    this.wrapper.removeEventListener('pointerup', this.#onPointerUp);
    this.wrapper.removeEventListener('pointercancel', this.#onPointerUp);
    this.wrapper.releasePointerCapture(event.pointerId);
  };

  #updateFromPointer(event) {
    const rect = this.wrapper.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const rounded = Math.round(pct);
    this.range.value = String(rounded);
    this.#setCompare(rounded);
  }

  #setCompare(value) {
    this.wrapper.style.setProperty('--compare', String(value));
  }
}

if (!customElements.get('lp-proof-slider')) {
  customElements.define('lp-proof-slider', LpProofSlider);
}
